// import { queryOpenRouter, JURISDICTION_MODEL } from '@/lib/openrouter';
import { interpolatePrompt, parseJsonResponse } from './ai-utils';
import { queryBlocksAgent } from '@/lib/blocks-ai-agent';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ENRICH_SYSTEM_PROMPT = `You are an AI assistant helping to fill out a legal document form.
Your task is to analyze the provided form data and the user's prompt to generate useful context or suggestions for subsequent form steps.

CRITICAL FORMATTING RULES:
1. DATES: ALWAYS return dates in "YYYY-MM-DD" format (ISO 8601). Example: "2024-12-31".
2. SELECTIONS: For fields with predefined options (enums), your output values MUST EXACTLY MATCH one of the allowed options (case-sensitive).
3. BOOLEANS: Return true/false for boolean fields, not strings like "yes"/"no".

Return your response in a valid JSON format. Do not include any markdown formatting or explanations outside the JSON.`;

function buildEnrichUserMessage(
  interpolatedPrompt: string,
  formData: Record<string, unknown>,
  outputSchema?: string | null
): string {
  let userMessage = `Form Data: ${JSON.stringify(formData, null, 2)}

User Prompt: ${interpolatedPrompt}`;

  if (outputSchema?.trim()) {
    try {
      const schema = JSON.parse(outputSchema);
      userMessage += `\n\nRequired Output Schema:\n${JSON.stringify(schema, null, 2)}\n\nYou MUST return a JSON object that matches this schema exactly. Include all required properties with appropriate values based on the form data and prompt.`;
    } catch {
      // invalid schema – ignore
    }
  } else {
    userMessage += '\n\nGenerate a JSON response based on the prompt.';
  }

  return userMessage;
}

export async function enrichContext(
  prompt: string,
  formData: Record<string, unknown>,
  outputSchema?: string | null,
  signal?: AbortSignal
): Promise<Record<string, unknown>> {
  const interpolated = interpolatePrompt(prompt, formData);
  const userMessage = buildEnrichUserMessage(interpolated, formData, outputSchema);

  // const response = await queryOpenRouter({
  //   messages: [
  //     { role: 'system', content: ENRICH_SYSTEM_PROMPT },
  //     { role: 'user', content: userMessage },
  //   ],
  //   model: JURISDICTION_MODEL,
  //   responseFormat: 'json',
  //   signal,
  // });

  const agentId = import.meta.env.VITE_ENRICH_CONTEXT_AI_AGENT_ID;
  if (!agentId) {
    throw new Error('VITE_ENRICH_CONTEXT_AI_AGENT_ID is not configured');
  }
  const response = await queryBlocksAgent(agentId, userMessage, signal);
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug('[enrichContext] raw response.message:', response?.message);
  }

  if (!response?.message) {
    throw new Error('AI returned an empty response');
  }

  try {
    return parseJsonResponse(response.message);
  } catch (parseError) {
    console.error(
      '[enrichContext] parse failed:',
      parseError,
      'raw message:',
      response.message
    );
    throw new Error(
      `Failed to parse AI response as JSON. Raw message: ${response.message.slice(0, 500)}`
    );
  }
}
